import { useCallback, useEffect } from "react";
import { Icon } from "@/components/Icon/Icon";
import { ScatterPlot } from "@/components/Plots";
import { useFlavorSpaceTrail } from "@/hooks/useFlavorSpaceTrail";
import { useWorkbenchViz } from "@/hooks/useWorkbenchViz";
import { logEvent } from "@/lib/eventLogger";
import { SMELL_DETAIL } from "@/services/mock";
import type { CompareSensory } from "@/utils/types/compare";
import type { VizPayload } from "@/services/workbench_api";
import { SensoryRadarStack } from "./SensoryRadarStack";

// AF "proposed" overlay color — warm amber-orange that reads as
// "temporary, can be reverted". Pure visual cue; values themselves are
// the dry-run viz from the SSE result event.
const PROPOSAL_COLOR = "#e07a26";
const PROPOSAL_COLOR_INK = "#9a4d10";

type RadarSelection =
  | { kind: "receptor"; receptor: string; armed: boolean }
  | { kind: "detail"; receptor: string; detail: string }
  | null;

/** The resolved selection passed to consumers (IngredientSearch). Maps the
 *  radar's symbolic selection (receptor/detail names) into the dim indices
 *  the suggestion algorithms need, so the consumer doesn't have to know
 *  anything about the axes payload layout. */
export type ActiveRadarSelection =
  | { kind: "receptor"; group: "taste" | "smell"; label: string; dimIdxs: number[] }
  | { kind: "detail"; group: "taste" | "smell"; label: string; dimIdx: number };

type SensoryAnalysisRailProps = {
  flavorId: string | null;
  formulaId: number | null;
  versionId: number | null;
  comparisons?: CompareSensory[];
  /** When set, this payload is rendered in place of `useWorkbenchViz`.
   *  Used by the AF Review state to show the proposed formulation's
   *  dry-run viz (loss, drCoord, radars) without persisting a version. */
  aiVizOverride?: VizPayload | null;
  /** When true, the Current series in scatter + radar renders in orange. */
  proposalMode?: boolean;
  /** Fires whenever the active radar selection changes — most-recently-
   *  changed radar wins. WorkbenchPage routes this into IngredientSearch
   *  so the "Recommended" pinned section reflects the user's vertex pick.
   *  Null when nothing is selected on either radar. */
  onActiveSelectionChange?: (sel: ActiveRadarSelection | null) => void;
};

// Converts `Record<string, number>` from the server into the `number[]`
// shape SensoryRadarStack expects (values aligned to the axes[] order).
function recordToAxisArray(
  values: Record<string, number> | null,
  axes: string[],
): number[] {
  // null → empty array, NOT a zero-filled array of axes.length. Empty makes
  // SR3Plot's polygon + per-axis vertex map render nothing, which is the
  // visual we want for "no current formulation" (or "smell radar not yet
  // implemented"). A zero-filled array would draw a degenerate polygon at
  // the centre.
  if (!values) return [];
  return axes.map((k) => values[k] ?? 0);
}

export function SensoryAnalysisRail({
  flavorId,
  formulaId,
  versionId,
  comparisons = [],
  aiVizOverride = null,
  proposalMode = false,
  onActiveSelectionChange,
}: SensoryAnalysisRailProps) {
  const { data: vizData, isLoading, isError } = useWorkbenchViz({
    flavorId,
    formulaId,
    versionId,
  });
  // When AF is in Review, swap the live viz for the proposal payload.
  // The override carries proposal-specific drCoord / radars / loss;
  // the live viz contributes target + neighbors + axes (background data
  // that doesn't change with the proposal).
  const data = aiVizOverride ?? vizData;

  // Convert and emit on every radar change. Most-recently-touched radar
  // wins; we don't track per-group selection in state because consumers
  // (IngredientSearch) only ever consume the active one.
  const emit = useCallback(
    (group: "taste" | "smell", sel: RadarSelection) => {
      if (!onActiveSelectionChange) return;
      if (sel == null) {
        onActiveSelectionChange(null);
        return;
      }
      const axesGroup = data?.axes[group] ?? null;
      const resolved = resolveSelection(sel, group, axesGroup);
      onActiveSelectionChange(resolved);
    },
    [onActiveSelectionChange, data?.axes],
  );

  // Clear the recommendation panel when the rail unmounts (phase switch,
  // navigation) so a stale selection from a prior session doesn't survive
  // the remount and seed a fetch against the new viz.
  useEffect(() => {
    return () => {
      onActiveSelectionChange?.(null);
    };
  }, [onActiveSelectionChange]);

  // T16: path trail — tracks Current's drCoord across edits. Reset key is
  // flavorId only (hotspotWeight isn't surfaced client-side on this component).
  // flavorId is the primary reset trigger (switching flavors wipes the trail).
  const trailResetKey = `${flavorId ?? "none"}`;
  const currentDrCoord = data?.drCoord ? { x: data.drCoord.x, y: data.drCoord.y } : null;
  const trail = useFlavorSpaceTrail(currentDrCoord, trailResetKey);

  const scatterOverlays = comparisons.map((c) => ({
    id: c.id,
    name: c.name,
    color: c.color,
    x: c.x,
    y: c.y,
  }));

  return (
    <div className="panel sensory-rail">
      <div className="sensory-rail-head">
        <div className="hstack gap-2">
          <Icon name="atom" size={14} />
          <span className="label">Sensory analysis</span>
        </div>
      </div>

      <div className="sensory-rail-body">
        {isLoading || !data ? (
          <EmptyState>Loading…</EmptyState>
        ) : data.status === "computing" || data.status === "pending" ? (
          <EmptyState>Computing…</EmptyState>
        ) : isError || data.status === "failed" ? (
          <EmptyState>
            Couldn’t load sensory data. Please try again or contact admin if
            this persists.
          </EmptyState>
        ) : (
          <div className="vstack gap-4">
            <div className="sensory-card">
              <div className="sensory-card-head">
                <span className="label">Flavor space</span>
              </div>
              <ScatterPlot
                neighbors={data.neighbors.map((n) => ({
                  id: String(n.id),
                  name: n.name ?? `#${n.id}`,
                  family: n.family,
                  x: n.drCoord.x,
                  y: n.drCoord.y,
                  isNearest: n.isNearest,
                  isLabeled: n.isLabeled,
                  loss: n.loss,                // T15: required for iso-loss ring computation
                }))}
                target={
                  data.target?.drCoord
                    ? { x: data.target.drCoord.x, y: data.target.drCoord.y }
                    : null
                }
                current={data.drCoord ? { x: data.drCoord.x, y: data.drCoord.y } : null}
                comparisons={scatterOverlays}
                width={344}
                height={280}
                targetName={data.target?.name ?? "Target"}
                currentName={proposalMode ? "Proposed" : "Working flask"}
                currentColor={proposalMode ? PROPOSAL_COLOR : undefined}
                currentColorInk={proposalMode ? PROPOSAL_COLOR_INK : undefined}
                trail={trail}
                onFlavorClick={(n) => {
                  logEvent("flavor_workbench_flavor_space_clicked", {
                    flavor_id: flavorId,
                    neighbor_flavor_id: n.id,
                    neighbor_flavor_name: n.name,
                  });
                }}
              />
            </div>

            <div className="sensory-card radar">
              <SensoryRadarStack
                label={`Taste · ${data.axes.taste.keys.length} axes`}
                axes={data.axes.taste.keys}
                target={recordToAxisArray(data.target?.tasteRadar ?? null, data.axes.taste.keys)}
                current={recordToAxisArray(data.tasteRadar, data.axes.taste.keys)}
                // Server-provided per-group hotspot detail (Phase 3). Keys
                // match data.axes.taste.keys; each entry has anonymized
                // hotspot axes labelled "S1", "S2", … with target + current
                // values pulled from the underlying profile vectors.
                detailMap={data.axes.taste.detail}
                currentColor={proposalMode ? PROPOSAL_COLOR : undefined}
                currentColorInk={proposalMode ? PROPOSAL_COLOR_INK : undefined}
                onSelectionChange={(sel) => emit("taste", sel)}
              />
            </div>

            <div className="sensory-card radar">
              {/* Smell is pilot-locked at the arena level — the title +
                  directions stay crisp so the surface still reads as the
                  Smell analysis (see SensoryRadarStack's pilotLocked prop). */}
              <SensoryRadarStack
                label={`Smell · ${data.axes.smell.keys.length} axes`}
                axes={data.axes.smell.keys}
                target={recordToAxisArray(null, data.axes.smell.keys)}
                current={recordToAxisArray(data.smellRadar, data.axes.smell.keys)}
                detailMap={SMELL_DETAIL}
                currentColor={proposalMode ? PROPOSAL_COLOR : undefined}
                currentColorInk={proposalMode ? PROPOSAL_COLOR_INK : undefined}
                onSelectionChange={(sel) => emit("smell", sel)}
                pilotLocked
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Map a radar's emitted selection onto the resolved shape consumers can act
// on. Returns null when the receptor's detail isn't shipped yet (e.g. smell
// in pre-Phase 4 payloads) or the picked detail axis can't be matched.
function resolveSelection(
  sel: NonNullable<RadarSelection>,
  group: "taste" | "smell",
  axesGroup:
    | { detail: Record<string, { axes: string[]; dimIdxs: number[] }> }
    | null,
): ActiveRadarSelection | null {
  if (axesGroup == null) return null;
  const detail = axesGroup.detail[sel.receptor];
  if (!detail || detail.dimIdxs.length === 0) return null;

  if (sel.kind === "receptor") {
    return {
      kind: "receptor",
      group,
      label: sel.receptor,
      dimIdxs: detail.dimIdxs,
    };
  }
  const detailIdx = detail.axes.indexOf(sel.detail);
  if (detailIdx < 0) return null;
  const dimIdx = detail.dimIdxs[detailIdx];
  if (dimIdx == null) return null;
  return {
    kind: "detail",
    group,
    label: `${sel.receptor} · ${sel.detail}`,
    dimIdx,
  };
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: 24,
        textAlign: "center",
        fontSize: 12,
        color: "var(--ink-4)",
      }}
    >
      {children}
    </div>
  );
}
